import React, { useRef, useState } from "react";
import Dropdown from "./Dropdown";
import ReactDOM from "react-dom";
import "./Settings.css";
import {
  UI_FONT_SIZE_OPTIONS,
  applyUIFontSize,
  normalizeUIFontSize,
} from "../utils/fontSize";
import {
  COLOR_OPTIONS,
  THEME_SWATCHES,
  applyTheme,
  loadTheme,
  THEME_KEY,
} from "../utils/themes";
import { SOUND_GROUPS, sounds } from "../delight/sounds";
import {
  useSound,
  setSoundOn,
  setSoundGroup,
  setSoundVolume,
} from "../delight/useSound";
import {
  getSparkle,
  setSparkle,
  getReduceMotion,
  setReduceMotion,
  systemReducesMotion,
} from "../prefs";
import {
  loadNotes,
  saveNotes,
  loadFolders,
  saveFolders,
  parseBackup,
  mergeById,
} from "../utils/storage";

const Icon = ({ children, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const IconUpload = () => (
  <Icon>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </Icon>
);

const IconDownload = () => (
  <Icon>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </Icon>
);

const IconCheck = () => (
  <Icon size={12}>
    <polyline points="20 6 9 17 4 12" />
  </Icon>
);

const readSetting = (key, fallback) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};

// Save a setting in this browser and apply it
const useSetting = (value, storageKey, apply) => {
  React.useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(value));
    } catch {}
    apply(value);
  }, [value, storageKey, apply]);
};

// Sections in the Preferences window's left bar
const NAV_ITEMS = [
  { id: "appearance", label: "Appearance" },
  { id: "sound", label: "Sound" },
  { id: "motion", label: "Motion" },
  { id: "backup", label: "Backup" },
  { id: "about", label: "About" },
];

// What each sound group covers in ThinkLoop
const SOUND_GROUP_HINTS = {
  clicks: "Buttons, menus and the sparkle",
  replies: "Creating and finishing things",
  typing: "A soft tap for each key",
  hover: "A low note when the pointer finds a button",
};

// On/off switch: a sunken track that turns into the pressed accent.
// Switching on plays a rising pair, off a falling one (the Sounds switch plays its own).
const Switch = ({ checked, onChange, label, disabled = false, ownSound = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className="switch plays-own-sound"
    disabled={disabled}
    onClick={() => {
      if (!ownSound) sounds.play(checked ? "unthink" : "think");
      onChange(!checked);
    }}
  >
    <span className="switch__knob" />
  </button>
);

const SettingsRow = ({ name, hint, children }) => (
  <div className="pref-setting-row">
    <div className="pref-setting-info">
      <div className="pref-setting-label">{name}</div>
      {hint && <div className="pref-setting-hint">{hint}</div>}
    </div>
    {children}
  </div>
);

const Settings = ({ onClose }) => {
  const [themeVal, setThemeVal] = useState(loadTheme);
  const [fontSizeVal, setFontSizeVal] = useState(() =>
    normalizeUIFontSize(readSetting("settings:fontSize")),
  );
  const [section, setSection] = useState("appearance");
  const [importStatus, setImportStatus] = useState(null);
  const fileInputRef = useRef(null);
  const sound = useSound();
  const [sparkleOn, setSparkleOn] = useState(getSparkle);
  const [reduceMotion, setReduceMotionState] = useState(getReduceMotion);
  const systemMotion = systemReducesMotion();

  useSetting(themeVal, THEME_KEY, applyTheme);

  // Escape closes; the page behind stays still while the dialog is open
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);
  useSetting(fontSizeVal, "settings:fontSize", applyUIFontSize);

  const handleExportNotes = () => {
    try {
      const data = {
        app: "ThinkLoop",
        exportedAt: new Date().toISOString(),
        notes: loadNotes(),
        folders: loadFolders(),
      };
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `thinkloop-backup-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      sounds.play("done");
    } catch (e) {
      console.error("Failed to export notes", e);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const { notes, folders } = parseBackup(JSON.parse(await file.text()));
      const savedNotes = saveNotes(mergeById(loadNotes(), notes));
      const savedFolders = saveFolders(mergeById(loadFolders(), folders));
      if (!savedNotes || !savedFolders) {
        throw new Error("Browser storage is full");
      }
      sounds.play("done");
      setImportStatus({
        type: "success",
        message: `Imported ${notes.length} note${notes.length === 1 ? "" : "s"}${
          folders.length
            ? ` and ${folders.length} folder${folders.length === 1 ? "" : "s"}`
            : ""
        }.`,
      });
    } catch (err) {
      sounds.play("warn");
      setImportStatus({
        type: "error",
        message: `Couldn't import that file. ${err.message || ""}`.trim(),
      });
    }
  };

  const sections = {
    appearance: (
      <>
        <div className="pref-content-header">
          <span className="pref-content-title" id="settings-title">Appearance</span>
        </div>
        <div className="pref-settings-area">
          <div className="pref-setting-section-label">Theme</div>
          <div className="theme-grid" role="radiogroup" aria-label="Theme">
            {COLOR_OPTIONS.map((option) => {
              const swatch = THEME_SWATCHES[option.id];
              const isActive = themeVal === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  className={`theme-option theme-card${isActive ? " is-active" : ""}`}
                  onClick={() => setThemeVal(option.id)}
                >
                  <span
                    className="theme-preview"
                    style={{ background: swatch.bg }}
                    aria-hidden="true"
                  >
                    <span className="theme-preview-card" style={{ background: swatch.card }}>
                      <span className="theme-preview-line" style={{ background: swatch.accent }} />
                      <span className="theme-preview-line short" style={{ background: swatch.accent }} />
                    </span>
                    <span className="theme-preview-pill" style={{ background: swatch.accent }} />
                    {isActive && (
                      <span className="theme-preview-check">
                        <IconCheck />
                      </span>
                    )}
                  </span>
                  <span className="theme-option-label">{option.label}</span>
                  <span className="theme-option-mode">
                    {option.mode === "dark" ? "Dark" : "Light"}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="pref-setting-divider" />
          <SettingsRow name="Text size" hint="Scales the whole interface">
            <Dropdown
              options={UI_FONT_SIZE_OPTIONS}
              value={fontSizeVal}
              onChange={(v) => setFontSizeVal(normalizeUIFontSize(v))}
            />
          </SettingsRow>
        </div>
      </>
    ),
    sound: (
      <>
        <div className="pref-content-header">
          <span className="pref-content-title" id="settings-title">Sound</span>
        </div>
        <div className="pref-settings-area">
          <SettingsRow name="Sounds" hint="Quiet tones for presses and typing">
            <Switch
              label="Sounds"
              checked={sound.enabled}
              onChange={setSoundOn}
              ownSound
            />
          </SettingsRow>
          <SettingsRow name="Volume">
            <input
              type="range"
              className="slider"
              min="0"
              max="1"
              step="0.05"
              value={sound.volume}
              disabled={!sound.enabled}
              onChange={(e) => setSoundVolume(Number(e.target.value))}
              style={{ "--fill": `${sound.volume * 100}%` }}
              aria-label="Volume"
            />
          </SettingsRow>
          <div className="pref-setting-divider" />
          <div className="pref-setting-section-label">Play sounds for</div>
          {SOUND_GROUPS.map((g) => (
            <SettingsRow
              key={g.id}
              name={g.id === "replies" ? "Chimes" : g.label}
              hint={SOUND_GROUP_HINTS[g.id]}
            >
              <Switch
                label={g.label}
                checked={sound.groups[g.id]}
                disabled={!sound.enabled}
                onChange={(on) => setSoundGroup(g.id, on)}
              />
            </SettingsRow>
          ))}
        </div>
      </>
    ),
    motion: (
      <>
        <div className="pref-content-header">
          <span className="pref-content-title" id="settings-title">Motion</span>
        </div>
        <div className="pref-settings-area">
          <SettingsRow name="Click sparkle" hint="A pixel burst when you click empty space">
            <Switch
              label="Click sparkle"
              checked={sparkleOn}
              onChange={(on) => {
                setSparkle(on);
                setSparkleOn(on);
              }}
            />
          </SettingsRow>
          <SettingsRow
            name="Reduce motion"
            hint={
              systemMotion
                ? "Already on in your system settings"
                : "Turns off animations and springs"
            }
          >
            <Switch
              label="Reduce motion"
              checked={reduceMotion || systemMotion}
              disabled={systemMotion}
              onChange={(on) => {
                setReduceMotion(on);
                setReduceMotionState(on);
              }}
            />
          </SettingsRow>
        </div>
      </>
    ),
    backup: (
      <>
        <div className="pref-content-header">
          <span className="pref-content-title" id="settings-title">Backup</span>
        </div>
        <div className="pref-settings-area">
          <SettingsRow
            name="Export notes"
            hint="Notes are saved in this browser. Download a copy to keep them safe or move them to another device."
          >
            <button
              type="button"
              className="btn plays-own-sound"
              onClick={handleExportNotes}
            >
              <IconDownload />
              Export
            </button>
          </SettingsRow>
          <div className="pref-setting-divider" />
          <SettingsRow
            name="Import notes"
            hint="Notes with the same ID are replaced, everything else is kept."
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              onChange={handleImportFile}
              hidden
            />
            <button
              type="button"
              className="btn"
              onClick={() => fileInputRef.current?.click()}
            >
              <IconUpload />
              Import
            </button>
          </SettingsRow>
          {importStatus && (
            <p
              className={`settings-note settings-note--${importStatus.type}`}
              role="status"
            >
              {importStatus.message}
            </p>
          )}
        </div>
      </>
    ),
    about: (
      <>
        <div className="pref-content-header">
          <span className="pref-content-title" id="settings-title">About</span>
        </div>
        <div className="pref-settings-area">
          <div className="settings-about">
            <span className="settings-about-brand">ThinkLoop</span>
            <p className="pref-setting-hint">
              Notes and code snippets, saved in your browser.
            </p>
            <span className="settings-about-links">
              <a href="https://aynjel.com" target="_blank" rel="noopener noreferrer">
                Made by Angel Gutierrez
              </a>
              <span aria-hidden="true">·</span>
              <a
                href="https://modelloop-frontend.onrender.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Try ModelLoop
              </a>
            </span>
          </div>
        </div>
      </>
    ),
  };

  return ReactDOM.createPortal(
    <div className="pref-overlay" onClick={onClose}>
      <div
        className="pref-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pref-sheet-handle" aria-hidden="true" />
        <nav className="pref-sidebar" aria-label="Settings sections">
          <div className="pref-sidebar-title">Preferences</div>
          {NAV_ITEMS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={`pref-nav-item${section === id ? " active" : ""}`}
              aria-current={section === id ? "page" : undefined}
              onClick={() => setSection(id)}
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="pref-content">{sections[section]}</div>
        <button
          type="button"
          className="pref-close-btn"
          onClick={onClose}
          aria-label="Close settings"
        >
          ✕
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default Settings;
